import { useState } from 'react'
import AtmosphericHero from '@/components/marketing/AtmosphericHero'
import ScrollReveal from '@/components/marketing/ScrollReveal'
import Showcase from '@/components/marketing/features/showcase/Showcase'
import FeatureSection from '@/components/marketing/features/FeatureSection'
import FeatureFaq from '@/components/marketing/features/FeatureFaq'
import type { FeaturesHubProps } from '@/types/features'

type View = 'artist' | 'label'

export default function FeaturesIndex(props: FeaturesHubProps) {
  const [view, setView] = useState<View>('artist')
  const sections = props.sections.filter((s) => s.audience !== 'label' || view === 'label')

  return (
    <>
      <AtmosphericHero minHeight="48vh">
        <div className="mx-auto max-w-3xl text-center space-y-10">
          <span className="hero-eyebrow" style={{ justifyContent: 'center' }}>{props.hero.eyebrow}</span>
          <h1 className="hero-headline" style={{ fontSize: 'clamp(2.25rem, 5.5vw, 4.5rem)' }}>{props.hero.title}</h1>
          <p className="hero-body mx-auto">{props.hero.body}</p>
        </div>
      </AtmosphericHero>

      <section className="marketing-section">
        <div className="container mx-auto px-6">
          <div className="mb-6 flex justify-center">
            <div role="group" aria-label="Dashboard view" className="marketing-toggle">
              {(['artist', 'label'] as View[]).map((v) => (
                // wb-chosen is the selection (shade, and its line on the border this button now has); the
                // dot is the shape that keeps the pressed button legible when the shade alone is faint.
                <button key={v} type="button" aria-pressed={view === v} onClick={() => setView(v)}
                  className={`inline-flex items-center gap-2 rounded-[0.55rem] border px-5 py-2.5 text-sm font-bold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--wavbee-orange)] ${view === v ? 'wb-chosen' : 'border-transparent text-white/55 hover:text-white/85'}`}>
                  {view === v && <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />}
                  {v === 'artist' ? 'Artist' : 'Label'}
                </button>
              ))}
            </div>
          </div>
          <Showcase
            screen="home"
            variant={view}
            // One 16:9 screen — what a 1280×720 window shows above the fold — not
            // the whole page (owner, 2026-10-20).
            height={720}
            label={`The wavbee dashboard home for ${view === 'label' ? 'a label' : 'an artist'}, with sample data`}
          />
        </div>
      </section>

      {sections.map((section, index) => (
        <FeatureSection key={section.key} section={section} index={index} variant={view} />
      ))}

      <section className="marketing-section">
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <h2 className="marketing-section-title">Standouts</h2>
            <div className="mt-8 grid gap-6 md:grid-cols-2">
              {props.standouts.map((s) => (
                <a key={s.href} href={s.href} className="marketing-surface-card block h-full hover:border-white/20 transition-colors">
                  <h3 className="text-xl font-semibold text-white">{s.title}</h3>
                  <p className="mt-3 text-sm leading-7 text-white/72">{s.summary}</p>
                  <p className="mt-4 text-sm font-semibold text-white">Read more →</p>
                </a>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      <FeatureFaq faq={props.faq} />

      <section className="marketing-section">
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="marketing-final-cta">
              <div>
                <h2 className="marketing-section-title">{props.cta.title}</h2>
                <p className="marketing-lead max-w-2xl">{props.cta.body}</p>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                {props.cta.actions.map((action, i) => (
                  <a key={action.href} href={action.href} className={i === 0 ? 'marketing-button-primary' : 'marketing-button-secondary'}>
                    {action.label}
                  </a>
                ))}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </>
  )
}
